import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'

import { DEFAULT_TZ } from './billing'
import type { DayKey, Settings } from './types'

export const DAY_SEC = 86400

/**
 * The longest a single session may be, in seconds.
 *
 * Enforced in three places that must agree: the time_entries_max_length check
 * constraint, stop_timer (which caps rather than failing), and the client
 * effect that stops a running timer when it gets here. The database is the one
 * that actually decides -- the other two exist so a person is told what
 * happened instead of meeting a constraint name in a toast.
 */
export const MAX_SESSION_SEC = 10 * 3600

/* ---------- day keys ---------- */

/**
 * Which calendar day an instant falls on, in the workspace's zone.
 *
 * This used to read the browser's own zone, and the server does not. Billing
 * pins the entry's date to Asia/Singapore -- entry_billing.entry_date is
 * `(started_at at time zone 'Asia/Singapore')::date`, and the CSV export
 * filters on that column -- so an entry near midnight landed on one date on
 * screen and a different one in the exported file, picked up a different
 * effective-dated rate, and fell into a different week or month. The totals a
 * client was invoiced from disagreed with the totals the report showed, for
 * anyone viewing from outside UTC+8.
 *
 * The zone is the workspace's, not the viewer's, because a day of work
 * belongs to the business's calendar rather than to wherever somebody happens
 * to be reading the report from.
 */
export function dayKey(d: Date): DayKey {
  return formatInTimeZone(d, DEFAULT_TZ, 'yyyy-MM-dd')
}

export function parseDayKey(k: DayKey): Date {
  const [y, m, d] = k.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function todayKey(): DayKey {
  return dayKey(new Date())
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}

/** Whole days between two day keys (a - b), ignoring DST/time. */
export function daysBetween(a: DayKey, b: DayKey): number {
  return Math.round((parseDayKey(a).getTime() - parseDayKey(b).getTime()) / 86400000)
}

/* ---------- weeks ---------- */

export function weekStartOf(offset: number, weekStart: Settings['weekStart'], from = new Date()): Date {
  const base = new Date(from.getFullYear(), from.getMonth(), from.getDate() + offset * 7)
  const dow = base.getDay()
  const back = weekStart === 'Monday' ? (dow === 0 ? 6 : dow - 1) : dow
  return addDays(base, -back)
}

export function weekKeys(offset: number, weekStart: Settings['weekStart'], from = new Date()): DayKey[] {
  const s = weekStartOf(offset, weekStart, from)
  const out: DayKey[] = []
  for (let i = 0; i < 7; i++) out.push(dayKey(addDays(s, i)))
  return out
}

/**
 * Every day key from `from` to `to` inclusive.
 *
 * The ends are sorted rather than rejected: somebody picking the later date
 * first means they want that range, not an error message. `max` caps the
 * length so a mistyped year cannot ask for ten thousand days -- the caller
 * compares the result's length against what it asked for to notice.
 */
export function keysBetween(from: DayKey, to: DayKey, max = 366): DayKey[] {
  const [a, b] = daysBetween(to, from) < 0 ? [to, from] : [from, to]
  const span = Math.min(daysBetween(b, a) + 1, max)
  const start = parseDayKey(a)
  const out: DayKey[] = []
  for (let i = 0; i < span; i++) out.push(dayKey(addDays(start, i)))
  return out
}

/* ---------- formatting ---------- */

/**
 * `h:mm` — every duration a person sees on screen.
 *
 * Seconds are deliberately not shown. They are still RECORDED to the second:
 * the database stores exact instants, and the CSV exports format their own
 * durations in the Edge Function, untouched by this. This is a display rule
 * only, so a figure here can be up to a minute short of the stored one.
 *
 * Truncated rather than rounded, so a duration never reads as longer than it
 * was -- an hour and fifty-nine seconds is "1:00", not "1:01".
 */
export function hms(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${h}:${String(m).padStart(2, '0')}`
}

/**
 * `h:mm:ss` — the LIVE running clock, and nothing else.
 *
 * The one place seconds still belong on screen: a clock that is visibly
 * counting has to move every second or it reads as stopped. Everywhere else
 * uses hms(), because a figure in a list does not need a precision nobody is
 * watching change.
 *
 * Truncated, like hms, so the clock never shows a second that has not elapsed.
 */
export function hmsExact(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

/** Duration in the workspace's chosen display format. */
export function formatDur(sec: number, fmt: Settings['durFormat']): string {
  if (fmt === 'decimal') return (sec / 3600).toFixed(2) + 'h'
  return hms(sec)
}

/** Decimal hours, e.g. `7.25h`. Used in reports regardless of the duration setting. */
export function hrs(sec: number): string {
  return (sec / 3600).toFixed(2) + 'h'
}

/** `h:mm` — the compact form the timesheet grid shows in its cells. */
export function hmShort(sec: number): string {
  if (!sec) return ''
  const h = Math.floor(sec / 3600)
  const m = Math.round((sec % 3600) / 60)
  return `${h}:${String(m).padStart(2, '0')}`
}

/** Seconds-since-midnight as a wall clock, honouring the 12h/24h setting. */
export function clock(sec: number, fmt: Settings['timeFormat'], withSeconds = false): string {
  const h24 = Math.floor(sec / 3600) % 24
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  const secPart = withSeconds ? `:${String(s).padStart(2, '0')}` : ''

  if (fmt === '24h') {
    return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}${secPart}`
  }
  const ap = h24 >= 12 ? 'PM' : 'AM'
  const h = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h}:${String(m).padStart(2, '0')}${secPart} ${ap}`
}

/* ---------- parsing ---------- */

/**
 * Accepts `9`, `9:30`, `9.30`, `0930`, `9:30 pm`, `21:30`, and now `13:25:07`.
 * Returns seconds since midnight, or null.
 *
 * Seconds are optional so nobody has to type them, but they round-trip when
 * present -- an entry recorded by the timer keeps its exact time through an
 * edit instead of being silently truncated to the minute.
 */
export function parseClock(v: string): number | null {
  const t = String(v).trim()
  if (!t) return null
  const m = t.match(/^(\d{1,2})[:.]?(\d{2})?(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i)
  if (!m) return null
  let h = Number(m[1])
  const mi = m[2] ? Number(m[2]) : 0
  const se = m[3] ? Number(m[3]) : 0
  if (mi > 59 || se > 59) return null
  if (m[4]) {
    const pm = m[4].toLowerCase().startsWith('p')
    if (h < 1 || h > 12) return null
    if (h === 12) h = 0
    if (pm) h += 12
  } else if (h > 23) return null
  return (h * 3600 + mi * 60 + se) % DAY_SEC
}

/** Accepts `1:30` (h:mm) or `1.5` / `1,5` (decimal hours). Returns seconds. */
export function parseDur(v: string): number {
  const t = String(v).trim().replace(',', '.')
  if (!t) return 0
  if (t.includes(':')) {
    const [a, b] = t.split(':')
    return (Number(a) || 0) * 3600 + (Number(b) || 0) * 60
  }
  return Math.round((parseFloat(t) || 0) * 3600)
}

/**
 * A duration a person typed into the editable h:mm:ss fields, in seconds.
 *
 * Deliberately NOT parseDur, which the timesheet grid uses: there a bare
 * number means HOURS, because the grid is where somebody fills in "7.5" for a
 * day. Here a bare number means MINUTES, because this field exists so that
 * setting an entry to ninety minutes does not require working out that it is
 * 1.5 and then that the end time is 16:05. Typing 90 means 90 minutes.
 *
 * Everything is computed in whole seconds from the typed value and nothing is
 * derived from the formatted string, so the stored duration is exactly what
 * was asked for rather than a re-parse of a rounded display.
 *
 * Null means "I could not read that" -- distinct from zero, which is read but
 * invalid, so the caller can say which.
 */
/**
 * A length in words, for showing what a typed duration was understood as.
 *
 * Words rather than digits, because the digits are the problem: a bare
 * number in parseDurationInput is MINUTES, so somebody who typed 3.3 meaning
 * three and a half hours gets 198 seconds. Shown back as "0:03" they see
 * what they expected; shown back as "3m 18s" they cannot.
 *
 * Seconds appear only when there is no hour component, so a short entry
 * never loses them and a long one is not cluttered by them.
 */
export function spellDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const sec = seconds % 60

  const parts: string[] = []
  if (h) parts.push(`${h}h`)
  if (m) parts.push(`${m}m`)
  if (sec && h === 0) parts.push(`${sec}s`)
  return parts.length ? parts.join(' ') : '0m'
}

export function parseDurationInput(value: string): number | null {
  const t = String(value).trim().toLowerCase().replace(/\s+/g, '')
  if (!t) return null

  // h:mm or h:mm:ss -- the form the field itself shows, so the likeliest retype.
  const clock = t.match(/^(\d{1,3}):([0-5]\d)(?::([0-5]\d))?$/)
  if (clock) {
    return Number(clock[1]) * 3600 + Number(clock[2]) * 60 + Number(clock[3] ?? 0)
  }

  // 2h, 90m, 1h30m, 45s, 1.5h, and combinations of them.
  const units = t.match(/^(?:(\d+(?:\.\d+)?)h)?(?:(\d+(?:\.\d+)?)m)?(?:(\d+(?:\.\d+)?)s)?$/)
  if (units && (units[1] || units[2] || units[3])) {
    const h = Number(units[1] ?? 0)
    const m = Number(units[2] ?? 0)
    const sec = Number(units[3] ?? 0)
    return Math.round(h * 3600 + m * 60 + sec)
  }

  // 1h30 -- hours, then bare minutes, which people write without the trailing m.
  const hm = t.match(/^(\d+)h(\d{1,2})$/)
  if (hm) return Number(hm[1]) * 3600 + Number(hm[2]) * 60

  // A bare number is minutes. This is the point of the whole field.
  const bare = t.match(/^(\d+(?:\.\d+)?)$/)
  if (bare) return Math.round(Number(bare[1]) * 60)

  return null
}

/**
 * Where an entry of `seconds` should sit, when its length is retyped.
 *
 * The obvious answer -- keep the start, move the end -- is right until the new
 * end lands in the future, which validate_time_entry refuses outright. Setting
 * a twelve-second entry from a minute ago to ninety minutes would otherwise
 * just fail: the time is plainly real, and the only thing wrong with it is
 * which direction the block was grown in.
 *
 * So when the end would run past now, the block is anchored on its END and
 * grown BACKWARDS instead. Either way the duration is exactly what was asked
 * for; only the direction changes.
 *
 * Returns the day as well as the start, because growing backwards can reach
 * into the previous day, and an entry's day is what anchors its start.
 */
export function fitDuration(
  day: DayKey,
  start: number,
  currentDur: number,
  seconds: number,
  nowMs: number = Date.now(),
): { day: DayKey; start: number; dur: number; movedBack: boolean } {
  const startMs = Date.parse(instantOf(day, start))
  const endMs = startMs + seconds * 1000

  if (endMs <= nowMs) return { day, start, dur: seconds, movedBack: false }

  // min() with now in case the stored end is itself somehow in the future --
  // anchoring on it would walk straight back into the error being avoided.
  const anchorMs = Math.min(startMs + currentDur * 1000, nowMs)
  const newStartMs = anchorMs - seconds * 1000

  return {
    day: dayKey(new Date(newStartMs)),
    start: secOfDay(newStartMs),
    dur: seconds,
    movedBack: true,
  }
}

/**
 * Seconds rounded to the nearest whole minute, never below one.
 *
 * "Nearest", so 90 seconds becomes two minutes rather than one -- rounding
 * down would quietly lose time on every stop. The floor of one minute exists
 * because a zero-length entry cannot be stored: the database requires an end
 * strictly after the start, so a twenty-second session has to become a minute
 * rather than nothing.
 */
export function roundToMinute(seconds: number): number {
  const whole = Math.max(0, Math.round(seconds))
  return Math.max(60, Math.round(whole / 60) * 60)
}

/**
 * Now, to the nearest minute, as seconds since local midnight.
 *
 * The default end of a manual entry: people say "I finished just now", not
 * "I finished at 15:04:37", and a manual range with seconds in it is noise.
 *
 * Rounding up within the last half-minute of the day would land on 86400 --
 * midnight TOMORROW expressed as a time today, which reads as 00:00 and would
 * put the end a day earlier than meant. Held at 23:59 instead.
 */
export function nowRoundedToMinute(nowMs: number = Date.now()): number {
  const rounded = Math.round(secOfDay(nowMs) / 60) * 60
  return rounded >= DAY_SEC ? DAY_SEC - 60 : rounded
}

/* ---------- labels ---------- */

export function dayLabel(k: DayKey): string {
  const diff = daysBetween(todayKey(), k)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  if (diff === -1) return 'Tomorrow'
  const d = parseDayKey(k)
  if (Math.abs(diff) < 7) return d.toLocaleDateString(undefined, { weekday: 'long' })
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })
}

/**
 * The date beside a day heading, e.g. `30 Sept 2026`.
 *
 * The year is spelled out rather than left to context. The list runs back
 * indefinitely, and "30 Sept" on a row from last year reads as this year --
 * which is exactly the kind of mistake nobody notices while reconciling.
 */
export function shortDate(k: DayKey): string {
  return parseDayKey(k).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** Seconds since local midnight for an epoch timestamp. */
/** Seconds since that day's midnight, in the workspace's zone. */
export function secOfDay(ms: number): number {
  const d = toZonedTime(new Date(ms), DEFAULT_TZ)
  return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds()
}

/**
 * The inverse of dayKey + secOfDay: a workspace day and a time within it,
 * back to the UTC instant the database stores.
 *
 * It lives here beside its inverse rather than in the data layer, so the two
 * cannot drift apart and so the round trip can be tested without a database.
 */
export function instantOf(day: DayKey, seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  const pad = (n: number) => String(n).padStart(2, '0')
  const wall = `${day}T${pad(Math.floor(whole / 3600) % 24)}:${pad(Math.floor(whole / 60) % 60)}:${pad(whole % 60)}`
  // Whole days beyond the first are added afterwards, so a value at or past
  // midnight lands on the following day rather than wrapping onto this one.
  const extraDays = Math.floor(whole / DAY_SEC)
  const base = fromZonedTime(wall, DEFAULT_TZ)
  return new Date(base.getTime() + extraDays * DAY_SEC * 1000).toISOString()
}
